import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/api";
import { z } from "zod";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  Edit,
  Trash2,
  PlusCircle,
  RefreshCw,
  Check,
  X,
  Coins,
  Gem,
} from "lucide-react";
import { ImageUploadField } from "@/components/ui/image-upload-field";

const CurrencyType = {
  Regular: "regular",
  Premium: "premium",
} as const;

type CurrencyType = typeof CurrencyType[keyof typeof CurrencyType];

type FormData = {
  name: string;
  imageUrl: string;
  isActive: boolean;
  price: number;
  currencyType: CurrencyType;
};

type CardBack = FormData & {
  id: number;
};

const cardBackFormSchema = z.object({
  name: z
    .string()
    .min(1, "Card back name is required")
    .max(50, "Card back name too long"),
  imageUrl: z.string().min(1, "Image URL is required"),
  isActive: z.boolean(),
  price: z.number().nonnegative(),
  currencyType: z.enum([CurrencyType.Regular, CurrencyType.Premium]),
});

export function CardBacksTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedCardBack, setSelectedCardBack] = useState<CardBack | null>(null);
  const [formData, setFormData] = useState<FormData>({
    name: "",
    imageUrl: "",
    isActive: true,
    price: 0,
    currencyType: CurrencyType.Regular,
  });

  // Query to get all card backs
  const {
    data: cardBacks,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["/api/card-backs"],
    queryFn: () => apiRequest<CardBack[]>("/api/card-backs"),
  });

  // Add new card back mutation
  const addCardBackMutation = useMutation({
    mutationFn: async (data: FormData) => {
      return apiRequest("/api/card-backs", {
        method: "POST",
        body: JSON.stringify(data),
        headers: {
          "Content-Type": "application/json",
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/card-backs"] });
      setIsAddDialogOpen(false);
      resetForm();
      toast({
        title: "Success",
        description: "Card back added successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to add card back",
        variant: "destructive",
      });
    },
  });

  // Update card back mutation
  const updateCardBackMutation = useMutation({
    mutationFn: async (data: CardBack) => {
      const { id, ...updateData } = data; // Extract ID and send only the update data
      return apiRequest(`/api/card-backs/${id}`, {
        method: "PUT",
        body: JSON.stringify(updateData),
        headers: {
          "Content-Type": "application/json",
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/card-backs"] });
      setIsEditDialogOpen(false);
      resetForm();
      toast({
        title: "Success",
        description: "Card back updated successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update card back",
        variant: "destructive",
      });
    },
  });

  // Delete card back mutation
  const deleteCardBackMutation = useMutation({
    mutationFn: (id: number) =>
      apiRequest(`/api/card-backs/${id}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/card-backs"] });
      toast({
        title: "Success",
        description: "Card back deleted successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete card back",
        variant: "destructive",
      });
    },
  });

  const handleAddSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const validatedData = cardBackFormSchema.parse(formData);
      addCardBackMutation.mutate(validatedData);
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation Error",
          description: error.errors[0].message,
          variant: "destructive",
        });
      }
    }
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCardBack) return;

    try {
      const validatedData = cardBackFormSchema.parse(formData);
      updateCardBackMutation.mutate({
        ...validatedData,
        id: selectedCardBack.id,
      });
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation Error",
          description: error.errors[0].message,
          variant: "destructive",
        });
      }
    }
  };

  const handleDelete = (id: number) => {
    deleteCardBackMutation.mutate(id);
  };

  const openEditDialog = (cardBack: CardBack) => {
    setSelectedCardBack(cardBack);
    setFormData({
      name: cardBack.name,
      imageUrl: cardBack.imageUrl,
      isActive: cardBack.isActive,
      price: Number(cardBack.price),
      currencyType: cardBack.currencyType,
    });
    setIsEditDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      imageUrl: "",
      isActive: true,
      price: 0,
      currencyType: CurrencyType.Regular,
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <RefreshCw className="animate-spin h-8 w-8" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-destructive/10 rounded-lg text-center">
        <h3 className="text-lg font-medium">Error loading card backs</h3>
        <p className="text-muted-foreground">{error.message}</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() =>
            queryClient.invalidateQueries({ queryKey: ["/api/card-backs"] })
          }
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={() => {
                resetForm();
                setIsAddDialogOpen(true);
              }}
            >
              <PlusCircle className="h-4 w-4 mr-2" />
              Add New Card Back
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>Add New Card Back</DialogTitle>
              <DialogDescription>
                Upload a new card back design and set its properties.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddSubmit}>
              <div className="grid gap-4 py-4">
                <div className="grid gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      placeholder="Enter card back name"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <ImageUploadField
                      id="imageUrl"
                      label="Image URL"
                      value={formData.imageUrl}
                      onChange={(value) =>
                        setFormData({ ...formData, imageUrl: value })
                      }
                      placeholder="Enter image URL or upload an image"
                      uploadEndpoint="/api/card-backs/upload-image"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="price">
                      Price ({formData.currencyType === CurrencyType.Premium
                        ? "Gems"
                        : "Coins"})
                    </Label>
                    <Input
                      id="price"
                      type="number"
                      min="0"
                      step="1"
                      pattern="\d*"
                      value={Math.floor(formData.price)}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          price: Math.floor(parseInt(e.target.value) || 0),
                        })
                      }
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="currencyType">Currency Type</Label>
                    <Select
                      value={formData.currencyType}
                      onValueChange={(value: CurrencyType) =>
                        setFormData({ ...formData, currencyType: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select currency type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={CurrencyType.Regular}>
                          <span className="flex items-center">
                            <Coins className="h-4 w-4 mr-2 text-yellow-500" />
                            Regular (Coins)
                          </span>
                        </SelectItem>
                        <SelectItem value={CurrencyType.Premium}>
                          <span className="flex items-center">
                            <Gem className="h-4 w-4 mr-2 text-purple-500" />
                            Premium (Gems)
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="isActive"
                    checked={formData.isActive}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, isActive: checked })
                    }
                  />
                  <Label htmlFor="isActive">Active</Label>
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsAddDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={addCardBackMutation.isPending}
                  className="ml-2"
                >
                  {addCardBackMutation.isPending ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    <>Add Card Back</>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Table>
        <TableCaption>A list of all card backs in the system.</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead>ID</TableHead>
            <TableHead>Preview</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Price</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {!cardBacks || cardBacks.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center">
                No card backs found. Add your first card back to get started.
              </TableCell>
            </TableRow>
          ) : (
            cardBacks.map((cardBack) => (
              <TableRow key={cardBack.id}>
                <TableCell className="font-medium">{cardBack.id}</TableCell>
                <TableCell>
                  <div className="w-12 h-16 relative">
                    <img
                      src={cardBack.imageUrl}
                      alt={cardBack.name}
                      className="absolute inset-0 w-full h-full object-contain"
                      onError={(e) => {
                        e.currentTarget.src = "placeholder-image.svg";
                      }}
                    />
                  </div>
                </TableCell>
                <TableCell>{cardBack.name}</TableCell>
                <TableCell>
                  <div className="flex items-center">
                    {cardBack.currencyType === "premium" ? (
                      <Gem className="h-4 w-4 mr-1 text-purple-500" />
                    ) : (
                      <Coins className="h-4 w-4 mr-1 text-yellow-500" />
                    )}
                    {parseFloat(cardBack.price.toString()) > 0 ? (
                      <span>
                        {Math.floor(parseFloat(cardBack.price.toString()))}{" "}
                        {cardBack.currencyType === CurrencyType.Premium
                          ? "Gems"
                          : "Coins"}
                      </span>
                    ) : (
                      "Free"
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  {cardBack.isActive ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-green-100 text-green-800">
                      <Check className="h-3 w-3 mr-1" />
                      Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-red-100 text-red-800">
                      <X className="h-3 w-3 mr-1" />
                      Inactive
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end space-x-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => openEditDialog(cardBack)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive" size="sm">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Card Back</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete "{cardBack.name}"? This
                            action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(cardBack.id)}
                            className="bg-destructive text-destructive-foreground"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Edit Card Back</DialogTitle>
            <DialogDescription>
              Update the details for this card back.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Name</Label>
                  <Input
                    id="edit-name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    placeholder="Enter card back name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <ImageUploadField
                    id="imageUrl"
                    label="Image URL"
                    value={formData.imageUrl}
                    onChange={(value) =>
                      setFormData({ ...formData, imageUrl: value })
                    }
                    placeholder="Enter image URL or upload an image"
                    uploadEndpoint="/api/card-backs/upload-image"
                  />
                  {formData.imageUrl && (
                    <div className="mt-2">
                      <Label>Current Image:</Label>
                      <div className="w-20 h-28 relative mt-1">
                        <img
                          src={formData.imageUrl}
                          alt={formData.name}
                          className="absolute inset-0 w-full h-full object-contain"
                          onError={(e) => {
                            e.currentTarget.src = "placeholder-image.svg";
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-price">
                    Price ({formData.currencyType === CurrencyType.Premium
                      ? "Gems"
                      : "Coins"})
                  </Label>
                  <Input
                    id="edit-price"
                    type="number"
                    min="0"
                    step="1"
                    pattern="\d*"
                    value={Math.floor(formData.price)}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        price: Math.floor(parseInt(e.target.value) || 0),
                      })
                    }
                    placeholder="100"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-currencyType">Currency Type</Label>
                  <Select
                    value={formData.currencyType}
                    onValueChange={(value: CurrencyType) =>
                      setFormData({ ...formData, currencyType: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select currency type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={CurrencyType.Regular}>
                        <span className="flex items-center">
                          <Coins className="h-4 w-4 mr-2 text-yellow-500" />
                          Regular (Coins)
                        </span>
                      </SelectItem>
                      <SelectItem value={CurrencyType.Premium}>
                        <span className="flex items-center">
                          <Gem className="h-4 w-4 mr-2 text-purple-500" />
                          Premium (Gems)
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="edit-isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, isActive: checked })
                  }
                />
                <Label htmlFor="edit-isActive">Active</Label>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={updateCardBackMutation.isPending}
                className="ml-2"
              >
                {updateCardBackMutation.isPending ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>Save Changes</>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}